import { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, Loader2, ExternalLink } from 'lucide-react';
import { useNotifications } from '../../context/NotificationsContext';
import NotificationItem from './NotificationItem';

/**
 * NotificationDropdown Component
 * Dropdown showing list of notifications (limited to 5 most recent)
 * Draggable, resizable, and constrained to window bounds
 */
const NotificationDropdown = ({ onClose, onNotificationClick, onNavigate }) => {
    const {
        notifications,
        loading,
        unreadCount,
        markAllNotificationsAsRead,
        markNotificationAsRead
    } = useNotifications();

    const dropdownRef = useRef(null);
    const [position, setPosition] = useState(null);
    const [size, setSize] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState(null);
    const dragStart = useRef({ x: 0, y: 0 });
    const elementStart = useRef({ x: 0, y: 0, width: 0, height: 0 });

    // Check if mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

    // Initialize position on mount
    useEffect(() => {
        if (dropdownRef.current && !position && !isMobile) {
            const rect = dropdownRef.current.getBoundingClientRect();
            // Keep initial position (right-aligned under bell)
            setPosition({ x: rect.left, y: rect.top });
            setSize({ width: rect.width, height: rect.height });
        }
    }, [position, isMobile]);

    // Constrain to window bounds
    const constrainToWindow = (x, y, width, height) => {
        const maxX = window.innerWidth - width;
        const maxY = window.innerHeight - height;
        return {
            x: Math.max(0, Math.min(x, maxX)),
            y: Math.max(0, Math.min(y, maxY))
        };
    };

    // Handle drag
    const handleDragStart = (e) => {
        if (isMobile || e.button !== 0) return;
        e.preventDefault();

        const rect = dropdownRef.current.getBoundingClientRect();
        if (!position) {
            setPosition({ x: rect.left, y: rect.top });
        }
        elementStart.current = { x: position?.x ?? rect.left, y: position?.y ?? rect.top };
        dragStart.current = { x: e.clientX, y: e.clientY };
        setIsDragging(true);
    };

    // Handle resize start
    const handleResizeStart = (e, direction) => {
        if (isMobile || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const rect = dropdownRef.current.getBoundingClientRect();
        if (!position) {
            setPosition({ x: rect.left, y: rect.top });
        }
        if (!size) {
            setSize({ width: rect.width, height: rect.height });
        }
        elementStart.current = {
            x: position?.x ?? rect.left,
            y: position?.y ?? rect.top,
            width: size?.width ?? rect.width,
            height: size?.height ?? rect.height
        };
        dragStart.current = { x: e.clientX, y: e.clientY };
        setResizeDirection(direction);
        setIsResizing(true);
    };

    // Mouse move handler
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && dropdownRef.current) {
                const deltaX = e.clientX - dragStart.current.x;
                const deltaY = e.clientY - dragStart.current.y;
                const rect = dropdownRef.current.getBoundingClientRect();
                const constrained = constrainToWindow(
                    elementStart.current.x + deltaX,
                    elementStart.current.y + deltaY,
                    rect.width,
                    rect.height
                );
                setPosition(constrained);
            }

            if (isResizing && resizeDirection) {
                const deltaX = e.clientX - dragStart.current.x;
                const deltaY = e.clientY - dragStart.current.y;

                let newWidth = elementStart.current.width;
                let newHeight = elementStart.current.height;
                let newX = elementStart.current.x;
                let newY = elementStart.current.y;

                const minWidth = 280;
                const minHeight = 200;

                // Resize from edges
                if (resizeDirection.includes('e')) {
                    newWidth = Math.max(minWidth, elementStart.current.width + deltaX);
                    // Constrain to window right edge
                    newWidth = Math.min(newWidth, window.innerWidth - newX);
                }
                if (resizeDirection.includes('w')) {
                    const potentialWidth = elementStart.current.width - deltaX;
                    if (potentialWidth >= minWidth) {
                        newWidth = potentialWidth;
                        newX = elementStart.current.x + deltaX;
                        // Constrain to window left edge
                        if (newX < 0) {
                            newWidth += newX;
                            newX = 0;
                        }
                    }
                }
                if (resizeDirection.includes('s')) {
                    newHeight = Math.max(minHeight, elementStart.current.height + deltaY);
                    // Constrain to window bottom edge
                    newHeight = Math.min(newHeight, window.innerHeight - newY);
                }
                if (resizeDirection.includes('n')) {
                    const potentialHeight = elementStart.current.height - deltaY;
                    if (potentialHeight >= minHeight) {
                        newHeight = potentialHeight;
                        newY = elementStart.current.y + deltaY;
                        // Constrain to window top edge
                        if (newY < 0) {
                            newHeight += newY;
                            newY = 0;
                        }
                    }
                }

                setSize({ width: newWidth, height: newHeight });
                setPosition({ x: newX, y: newY });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setIsResizing(false);
            setResizeDirection(null);
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
            document.body.style.cursor = isDragging ? 'grabbing' :
                resizeDirection === 'se' || resizeDirection === 'nw' ? 'nwse-resize' :
                resizeDirection === 'sw' || resizeDirection === 'ne' ? 'nesw-resize' :
                resizeDirection === 'e' || resizeDirection === 'w' ? 'ew-resize' :
                resizeDirection === 'n' || resizeDirection === 's' ? 'ns-resize' : 'default';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
    }, [isDragging, isResizing, resizeDirection]);

    const handleMarkAllAsRead = async () => {
        await markAllNotificationsAsRead();
    };

    const handleNotificationClick = async (notification) => {
        const isRead = notification.isRead ?? notification.ISREAD;
        const notifId = notification.id || notification.ID;
        if (!isRead) {
            await markNotificationAsRead(notifId);
        }
        onNotificationClick?.(notification);
    };

    const handleMarkAsRead = async (notification) => {
        const notifId = notification.id || notification.ID;
        await markNotificationAsRead(notifId);
    };

    const handleViewAll = () => {
        onClose?.();
        onNavigate?.('notifications');
    };

    const unreadNotifications = notifications.filter(n => {
        const isRead = n.isRead ?? n.ISREAD;
        return !isRead;
    });

    const displayedNotifications = unreadNotifications.slice(0, 5);

    // Mobile: fullscreen
    if (isMobile) {
        return (
            <div className="fixed inset-0 z-50 bg-white flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-cabaza-purple" />
                        <span className="font-semibold text-gray-800">Notifications</span>
                        {unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                                {unreadCount}
                            </span>
                        )}
                    </div>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllAsRead}
                            className="text-xs text-cabaza-purple hover:underline flex items-center gap-1"
                        >
                            <CheckCheck className="w-3 h-3" />
                            Tout lu
                        </button>
                    )}
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto">
                    {loading && notifications.length === 0 ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-cabaza-purple animate-spin" />
                        </div>
                    ) : unreadNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                            <Bell className="w-10 h-10 mb-2 opacity-50" />
                            <p className="text-sm">Aucune nouvelle notification</p>
                        </div>
                    ) : (
                        displayedNotifications.map((notification) => (
                            <NotificationItem
                                key={notification.id || notification.ID}
                                notification={notification}
                                onClick={() => handleNotificationClick(notification)}
                                onMarkAsRead={handleMarkAsRead}
                                showMarkAsRead={true}
                            />
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <button
                        onClick={handleViewAll}
                        className="text-sm text-cabaza-purple hover:underline flex items-center gap-1"
                    >
                        <ExternalLink className="w-3 h-3" />
                        Voir tout{notifications.length > 0 ? ` (${notifications.length})` : ''}
                    </button>
                    <button
                        onClick={onClose}
                        className="text-sm text-gray-500 hover:text-gray-700"
                    >
                        Fermer
                    </button>
                </div>
            </div>
        );
    }

    // Desktop: positioned dropdown with drag/resize
    const style = position ? {
        position: 'fixed',
        left: position.x,
        top: position.y,
        ...(size ? { width: size.width, height: size.height } : {})
    } : {};

    return (
        <div
            ref={dropdownRef}
            style={style}
            className={`${position ? '' : 'absolute right-0 mt-2'} w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden flex flex-col`}
        >
            {/* Resize handles */}
            <div className="absolute -top-1 -left-1 w-3 h-3 cursor-nw-resize z-10" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
            <div className="absolute -top-1 -right-1 w-3 h-3 cursor-ne-resize z-10" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
            <div className="absolute -bottom-1 -left-1 w-3 h-3 cursor-sw-resize z-10" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
            <div className="absolute -bottom-1 -right-1 w-3 h-3 cursor-se-resize z-10" onMouseDown={(e) => handleResizeStart(e, 'se')} />
            <div className="absolute top-2 bottom-2 -left-1 w-2 cursor-ew-resize z-10" onMouseDown={(e) => handleResizeStart(e, 'w')} />
            <div className="absolute top-2 bottom-2 -right-1 w-2 cursor-ew-resize z-10" onMouseDown={(e) => handleResizeStart(e, 'e')} />
            <div className="absolute -top-1 left-2 right-2 h-2 cursor-ns-resize z-10" onMouseDown={(e) => handleResizeStart(e, 'n')} />
            <div className="absolute -bottom-1 left-2 right-2 h-2 cursor-ns-resize z-10" onMouseDown={(e) => handleResizeStart(e, 's')} />

            {/* Header - Draggable */}
            <div
                className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                onMouseDown={handleDragStart}
            >
                <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-cabaza-purple" />
                    <span className="font-semibold text-gray-800">Notifications</span>
                    {unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                            {unreadCount}
                        </span>
                    )}
                </div>

                {unreadCount > 0 && (
                    <button
                        onClick={handleMarkAllAsRead}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="text-xs text-cabaza-purple hover:underline flex items-center gap-1"
                    >
                        <CheckCheck className="w-3 h-3" />
                        Tout marquer comme lu
                    </button>
                )}
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: size ? undefined : '24rem' }}>
                {loading && notifications.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-cabaza-purple animate-spin" />
                    </div>
                ) : unreadNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                        <Bell className="w-10 h-10 mb-2 opacity-50" />
                        <p className="text-sm">Aucune nouvelle notification</p>
                    </div>
                ) : (
                    displayedNotifications.map((notification) => (
                        <NotificationItem
                            key={notification.id || notification.ID}
                            notification={notification}
                            onClick={() => handleNotificationClick(notification)}
                            onMarkAsRead={handleMarkAsRead}
                            showMarkAsRead={true}
                        />
                    ))
                )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <button
                    onClick={handleViewAll}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="text-sm text-cabaza-purple hover:underline flex items-center gap-1"
                >
                    <ExternalLink className="w-3 h-3" />
                    Voir tout{notifications.length > 0 ? ` (${notifications.length})` : ''}
                </button>
                <button
                    onClick={onClose}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="text-sm text-gray-500 hover:text-gray-700"
                >
                    Fermer
                </button>
            </div>
        </div>
    );
};

export default NotificationDropdown;
